import { createAsyncThunk } from "@reduxjs/toolkit";

const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));
const getCursor = (url) => {
    if (!url) return null;
    try {
        const u = new URL(url, window.location.origin);
        return u.searchParams.get("cursor");
    } catch {
        try {
            const u = new URL(url);
            return u.searchParams.get("cursor");
        } catch {
            return null;
        }
    }
};

export const fetchChatHistory = createAsyncThunk(
    "chat/fetchChatHistory",
    async (arg, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const { chatId, cursor } =
            typeof arg === "object" ? arg : { chatId: arg, cursor: null };
        const base = `${process.env.REACT_APP_BACKEND_URL}/chats/messages/${chatId}/`;
        const uri = cursor ? `${base}?cursor=${encodeURIComponent(cursor)}` : base;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        try {
            const response = await fetch(uri, options);
            if (!response.ok) {
                throw new Error("No se ha podido obtener el historial.");
            }
            const data = await response.json();
            const results = toList(data);
            const messagesAsc = results.length > 0 ? results.slice().reverse() : [];
            return {
                chatId,
                messages: messagesAsc,
                nextCursor: getCursor(data?.next),
                isLoadMore: Boolean(cursor),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const fetchChatUsers = createAsyncThunk(
    "chat/fetchChatUsers",
    async (_, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/users/`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        try {
            const response = await fetch(uri, options);
            if (!response.ok) {
                throw new Error("No se pudo obtener la lista de usuarios.");
            }
            const data = await response.json();
            return toList(data);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const initChat = createAsyncThunk(
    "chat/initChat",
    async ({ senderId, receiverId }, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/init/`;

        console.log("[initChat] Enviando POST a:", uri);
        console.log("[initChat] Payload:", {
            sender_id: senderId,
            receiver_id: receiverId,
        });

        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ sender_id: senderId, receiver_id: receiverId }),
        };
        try {
            const response = await fetch(uri, options);
            const data = await response.json();

            console.log("[initChat] Respuesta del servidor:", data);

            if (!response.ok) {
                throw new Error(data?.detail || "No se pudo iniciar el chat.");
            }
            return data;
        } catch (error) {
            console.error("[initChat] Error:", error.message);
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// GET Old Chats (DM + grupos)
export const fetchMyChats = createAsyncThunk(
    "chat/fetchUserChats",
    async (_, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/my/`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        try {
            const response = await fetch(uri, options);
            if (!response.ok) {
                throw new Error("No se pudieron obtener tus chats.");
            }
            const data = await response.json();
            return toList(data);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const fetchMoreChatHistory = createAsyncThunk(
    "chat/fetchMoreChatHistory",
    async (chatId, thunkAPI) => {
        const { historyMeta } = thunkAPI.getState().chat || {};
        const cursor = historyMeta?.[chatId]?.nextCursor;
        if (!cursor) {
            return { chatId, messages: [], nextCursor: null, isLoadMore: true };
        }
        return await thunkAPI
            .dispatch(fetchChatHistory({ chatId, cursor }))
            .unwrap();
    }
);

// 🔹 NUEVO: obtener lista de canales (grupos)
export const fetchChannels = createAsyncThunk(
    "chat/fetchChannels",
    async (_, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        try {
            const response = await fetch(uri, options);
            if (!response.ok) {
                throw new Error("No se pudieron obtener los canales.");
            }
            const data = await response.json();
            return toList(data);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// 🔹 NUEVO: crear canal (grupo)
export const createChannel = createAsyncThunk(
    "chat/createChannel",
    async ({ name, topic, participantIds }, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/`;

        const payload = {
            name,
            topic: topic || "",
            participants: Array.isArray(participantIds) ? participantIds : [],
        };

        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        };

        try {
            const response = await fetch(uri, options);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.detail || "No se pudo crear el canal.");
            }
            return data; // objeto Chat devuelto por el backend
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// 🔹 NUEVO: agregar usuarios a un canal existente
export const addChannelMembers = createAsyncThunk(
    "chat/addChannelMembers",
    async ({ channelId, participantIds }, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/${channelId}/members/`;

        const payload = {
            participants: Array.isArray(participantIds) ? participantIds : [],
        };

        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        };

        try {
            const response = await fetch(uri, options);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data?.detail || "No se pudieron agregar usuarios al canal."
                );
            }

            return data; // objeto Chat actualizado
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const removeChannelMembers = createAsyncThunk(
  "chat/removeChannelMembers",
  async ({ channelId, participantIds }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/${channelId}/members/`;

    const payload = {
      participants: Array.isArray(participantIds) ? participantIds : [],
    };

    const options = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || "No se pudieron quitar usuarios del canal.");
      }

      return data; // Chat actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔹 NUEVO: notificaciones → obtener mensajes no leídos por chat
export const fetchUnreadCounts = createAsyncThunk(
    "chat/fetchUnreadCounts",
    async (_, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/messages/unread/`;
        const options = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await fetch(uri, options);
            if (!response.ok) {
                throw new Error("No se pudieron obtener las notificaciones de chat.");
            }
            const data = await response.json();
            // backend devuelve típicamente: [{ chat_id: 1, count: 3 }, ...]
            return Array.isArray(data) ? data : [];
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// 🔹 NUEVO: notificaciones → marcar un chat como leído
export const markChatAsRead = createAsyncThunk(
    "chat/markChatAsRead",
    async (chatId, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/messages/${chatId}/read`;
        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await fetch(uri, options);
            const data = await response.json();
            if (!response.ok) {
                throw new Error(
                    data?.detail || "No se pudo marcar el chat como leído."
                );
            }
            return { chatId };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

// 🔹 Obtener mensajes no leídos por chat
export const fetchUnreadMessages = createAsyncThunk(
  "chat/fetchUnreadMessages",
  async (_, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/messages/unread/`;

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.detail || "No se pudieron obtener los mensajes no leídos."
        );
      }

      // data es una lista tipo: [{ chat_id: 1, count: 3 }, ...]
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateChannel = createAsyncThunk(
  "chat/updateChannel",
  async ({ channelId, channelData }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/${channelId}/`;

    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(channelData || {}),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || "No se pudo actualizar el canal.");
      }

      return data; // canal actualizado
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteChannel = createAsyncThunk(
  "chat/deleteChannel",
  async (channelId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/chats/channels/${channelId}/`;

    const options = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.detail || "No se pudo eliminar el canal.");
      }

      return { channelId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);